import Button from "@/common/components/Button";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListFilters from "@/common/components/ListFilters";
import Table from "@/common/components/Table";
import TextField from "@/common/components/TextField";
import { useCompanyUsers } from "@/common/hooks/queries/useCompanyUsers";
import useDebounce from "@/common/hooks/useDebounce";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { CompanyUsersTransformer } from "@/common/services/companyUsersTransformer";
import { Mapper } from "@/common/services/mapper";
import { CompanyBranchData } from "@/common/types/companyBranches.type";
import { RoleData } from "@/common/types/roles";
import { defaultColumns } from "@/features/privateCompanies/constants/companyUsersColumns.constant";
import { useCompanyUserRoles } from "@/features/privateCompanies/hooks/useCompanyUserRoles";
import { CompanyUserData, CompanyUsersTableItem } from "@/features/privateCompanies/types/companyUsers.type";
import { SearchIcon } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { userStatusOptions } from "../../CompanyGovernmentAllocations/Show/constants/userStatusOptions";
import BulkActionNewEntryModal from "@/common/components/BulkActionNewEntryModal";
import { useQueryClient } from "@tanstack/react-query";
import useCompany from "@/common/hooks/queries/useCompany";
import LoadingSpinner from "@/common/components/LoadingSpinner";
import FilterCheckbox from "@/common/components/FilterCheckbox";
import { useEffect } from "react";
import { useAvailableCompanyBranches } from "@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches";

const UserList = () => {
  const { id } = useParams();

  const { data: company } = useCompany({ id: id || '' });

  const queryClient = useQueryClient();

  const { filterParams, setFilterParams } = useFilterParams();

  const [search, setSearch] = useDebounce(filterParams.search || '');

  const [searchBranch, setSearchBranch] = useDebounce('');
  const [searchRole, setSearchRole] = useDebounce('');

  const {
    data: companyUsers,
    meta,
    isPending: isPendingCompanyUsers,
    isFetching: isFetchingCompanyUsers,
    error: companyUsersError = {} as any,
    refetch: refetchCompanyUsers,
  } = useCompanyUsers({
    filters: {
      page: filterParams.page || 1,
      search: search,
      companyId: id || '',
      branchId: filterParams.branch?.value,
      roleId: filterParams.role?.value,
      status: filterParams.status?.value,
      showDescendants: filterParams.showDescendants || undefined,
    },
    mapper: Mapper.mapWithTransformer<CompanyUserData[], CompanyUsersTableItem[]>({
      transformer: CompanyUsersTransformer.tableTransformer({
        companyId: id || '',
        operationKlassToCheckPermission: 'Admin::Client::Users::Show'
      }),
    }),
  });

  useEffect(() => {
    if (!filterParams.branch?.value) setFilterParams((prev) => ({ ...prev, showDescendants: null }))
  }, [filterParams.branch?.value])

  const { data: companyBranchOptions } = useAvailableCompanyBranches({
    companyId: id,
    filters: { search: searchBranch },
    mapper: Mapper.mapToOptions<CompanyBranchData>({
      labelFieldName: 'display_name',
      valueFieldName: 'id',
    }),
  });

  const { data: rolesOptions } = useCompanyUserRoles({
    filters: { search: searchRole },
    mapper: Mapper.mapToOptions<RoleData>({ labelFieldName: 'display_name', valueFieldName: 'id' })
  });

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-192px)]"
      statusCode={companyUsersError?.status}
      refetchFunction={refetchCompanyUsers}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <TextField
            name="search"
            LeftIcon={<SearchIcon />}
            placeholder="Pesquisar"
            className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
            onChange={(e) => {
              setSearch((e.target as HTMLInputElement).value);
              setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }));
            }}
            value={filterParams.search}
          />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ListFilters
                filters={[
                  {
                    className: 'w-[128px]',
                    onInputChange: setSearchRole,
                    onChange: (e) => setFilterParams((prev) => ({ ...prev, role: e })),
                    value: filterParams.role,
                    options: [{ label: 'Todos', value: '' }, ...rolesOptions],
                    placeholder: 'Perfil'
                  },
                  {
                    className: 'w-[128px]',
                    onInputChange: setSearchBranch,
                    onChange: (e) => setFilterParams((prev) => ({ ...prev, branch: e })),
                    value: filterParams.branch,
                    options: [{ label: 'Todas', value: '' }, ...(companyBranchOptions || [])],
                    placeholder: 'Filial',
                  },
                  {
                    className: 'w-[128px]',
                    onChange: (e) => setFilterParams((prev) => ({ ...prev, status: e })),
                    value: filterParams.status,
                    options: userStatusOptions,
                    placeholder: 'Status'
                  }
                ]}
              />
              <FilterCheckbox
                id="showDescendants"
                name="showDescendants"
                label="Mostrar descendentes"
                isChecked={!!filterParams.showDescendants}
                onChange={(showDescendants) =>
                  setFilterParams((prev) => ({ ...prev, showDescendants: showDescendants ? true : null }))
                }
                isDisabled={!filterParams.branch?.value}
              />
          </div>

            <div className="flex items-center gap-3">
              <LoadingSpinner isLoading={isFetchingCompanyUsers} />
              <BulkActionNewEntryModal
                operationKlassToCheckPermission="Admin::BulkActions::Entries::RequestTemplate"
                options={[
                  {
                    label: 'Cadastrar usuários',
                    actionerKlass: 'BulkActions::Admin::ClientUsersImporter',
                  },
                  {
                    label: 'Editar usuários',
                    actionerKlass: 'BulkActions::Admin::ClientUsersEditor',
                  },
                ]}
                onEntryCompleted={() => {
                  queryClient.invalidateQueries({ queryKey: ['companyUsers'] });
                }}
                globalColumnsDefaultValues={{
                  client_company_id: {
                    value: id || '',
                    label: company?.general_information?.fantasy_name || '',
                  }
                }}
              />

              <Link
                to={isPendingCompanyUsers ? '' : `/companies/${id}/users/create`}
                className={isPendingCompanyUsers ? 'cursor-default' : ''}
              >
                <Button
                  className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
                  data-testid="create_company_user"
                  disabled={isPendingCompanyUsers}
                  operationKlassToCheckPermission="Admin::Client::Users::Create"
                >
                  Novo usuário
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <Table
          className="max-h-[calc(100vh-324px)]"
          columns={defaultColumns}
          isLoadingData={isPendingCompanyUsers}
          page={filterParams.page || 1}
          rows={companyUsers}
          totalPages={meta.total_pages}
          totalCount={meta.total}
          hasPagination
          setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          rowEmptyValue="--"
        />
      </div>
    </ErrorMessageWrapper>
  );
};

export default UserList;
